import moment from 'moment';
import {RRule, rrulestr, type Weekday} from 'rrule';
import {capitalize} from 'underscore.string';

type DayIndex = 0 | 1 | 2 | 3 | 4 | 5 | 6; // Based on moment().day()

const DAY_TO_WEEKDAY: Record<DayIndex, Weekday> = {
    0: RRule.SU,
    1: RRule.MO,
    2: RRule.TU,
    3: RRule.WE,
    4: RRule.TH,
    5: RRule.FR,
    6: RRule.SA,
};

/*
 * Builds a weekly recurrence RRule string based on the weekday of the referenceDatetime.
 * If no referenceDatetime is provided, the current date will be used.
 * @param cycle - Weekly or Monthly.
 * @param referenceDate - The reference date to build the rule from.
 * @param occurrence? - The occurrence of the weekday.
 *    - If not provided, the first occurrence of the weekday will be used.
 * @param interval? - The interval of the recurrence.
 * @returns RRule - The RRule object.
 */
export function buildRRuleFromReferenceDate(
    cycle: 'weekly' | 'monthly',
    referenceDate: moment.Moment,
    occurrence?: number,
    interval?: number
): RRule {
    const dayIndex: DayIndex = referenceDate.day() as DayIndex;
    const weekday = DAY_TO_WEEKDAY[dayIndex];

    // Store as UTC to avoid issues with the RRule library's parsing
    // which interprets UTC dates in the local timezone
    // See README: https://github.com/jkbrzt/rrule#important-use-utc-dates
    const utcMoment = referenceDate.clone().utc();
    const dtstart = new Date(
        Date.UTC(
            utcMoment.year(),
            utcMoment.month(),
            utcMoment.date(),
            utcMoment.hour(),
            utcMoment.minute(),
            utcMoment.second()
        )
    );

    const rrule = new RRule({
        freq: cycle === 'weekly' ? RRule.WEEKLY : RRule.MONTHLY,
        interval: interval ?? undefined,
        byweekday: typeof occurrence === 'number' ? [weekday.nth(occurrence)] : [weekday],
        dtstart: dtstart,
    });

    return rrule;
}

type RuleOption = {
    label: string;
    rule: string;
};
export function getWeeklyRule(baseMoment: moment.Moment): RuleOption {
    const rrule = buildRRuleFromReferenceDate('weekly', baseMoment);
    const weekdayLabel = baseMoment.format('dddd');
    return {
        label: `Weekly on ${weekdayLabel}`,
        rule: rrule.toString(),
    };
}
export function getMonthlyRule(baseMoment: moment.Moment, occurrence: number): RuleOption {
    const rrule = buildRRuleFromReferenceDate('monthly', baseMoment, occurrence);
    const weekdayLabel = baseMoment.format('dddd');
    const occurrenceLabel = occurrence === -1 ? 'last' : moment().date(occurrence).format('Do');
    return {
        label: `Monthly on the ${occurrenceLabel} ${weekdayLabel}`,
        rule: rrule.toString(),
    };
}

export function getThreeMonthlyRule(baseMoment: moment.Moment, occurrence: number): RuleOption {
    const rrule = buildRRuleFromReferenceDate('monthly', baseMoment, occurrence, 3);
    return {
        label: capitalize(rrule.toText()),
        rule: rrule.toString(),
    };
}

type DefaultOptions = {
    weekly: RuleOption;
    monthly: RuleOption[];
    threeMonthly: RuleOption;
};
export function getDefaultOptions(referenceDatetime?: number | null): DefaultOptions {
    const baseUnix = referenceDatetime ?? moment().unix();
    const baseMoment = moment.unix(baseUnix);

    const dayOfMonth = baseMoment.date();
    const occurrence = Math.floor((dayOfMonth - 1) / 7) + 1;

    const isLastWeekdayOfMonth = !baseMoment.clone().add(7, 'days').isSame(baseMoment, 'month');

    const monthly: RuleOption[] = [];

    let threeMonthly = getThreeMonthlyRule(baseMoment, occurrence);
    if (occurrence < 5) {
        monthly.push(getMonthlyRule(baseMoment, occurrence));
    }

    if (isLastWeekdayOfMonth) {
        monthly.push(getMonthlyRule(baseMoment, -1));
        threeMonthly = getThreeMonthlyRule(baseMoment, -1);
    }

    return {
        weekly: getWeeklyRule(baseMoment),
        monthly,
        threeMonthly,
    };
}

export function getStartDateFromRRule(rule: string): number {
    return rrulestr(rule).options.dtstart.getTime() / 1000;
}

export function getNextSendDate(rule: string): Date | null {
    try {
        const parsed = rrulestr(rule);
        return parsed.after(new Date(), true);
    } catch {
        return null;
    }
}
